import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { Stripe, StripeCardElement } from '@stripe/stripe-js';
import { Link } from 'react-router-dom';
import AsyncButton from '../components/AsyncButton';
import '../TeamBilling.css';

export interface CardSummary {
  brand?: string;
  last4?: string;
  expMonth?: number;
  expYear?: number;
}

/** A saved-card one-liner: "visa •••• 4242 (exp 12/2030)" */
export const cardLabel = (card?: CardSummary): string => {
  if (!card || !card.last4) return '—';
  const exp =
    card.expMonth && card.expYear
      ? ` (exp ${card.expMonth}/${card.expYear})`
      : '';
  return `${card.brand || 'card'} •••• ${card.last4}${exp}`;
};

/** Two-line card cell: "visa •••• 4242" over a smaller expiry line */
export const CardCell: React.FC<{ card?: CardSummary }> = ({ card }) => {
  if (!card || !card.last4) return <>—</>;
  return (
    <div className="billing-card-cell">
      <span>
        {card.brand || 'card'} •••• {card.last4}
      </span>
      {card.expMonth && card.expYear && (
        <span className="billing-card-cell-exp">
          exp {card.expMonth}/{card.expYear}
        </span>
      )}
    </div>
  );
};

/**
 * Loads Stripe.js once, on demand, with the given publishable key. The
 * promise is cached in a ref so the SDK is fetched only when a card form is
 * actually opened, and only a single time per key.
 */
export function useStripeLoader(
  publishableKey: string,
): () => Promise<Stripe | null> {
  const stripePromiseRef = useRef<Promise<Stripe | null> | null>(null);
  return useCallback(() => {
    if (!stripePromiseRef.current) {
      stripePromiseRef.current = import('@stripe/stripe-js').then(
        ({ loadStripe }) => loadStripe(publishableKey),
      );
    }
    return stripePromiseRef.current;
  }, [publishableKey]);
}

interface StripeCardFormProps {
  getStripe: () => Promise<Stripe | null>;
  submitLabel?: string;
  busy?: boolean;
  error?: string | null;
  onSubmit?: (stripe: Stripe, card: StripeCardElement) => void;
  /** When set, renders a secondary button to the left of the submit button. */
  onCancel?: () => void;
  cancelLabel?: string;
  /**
   * Headless mode for flows whose own button commits the card (the team
   * wizard's Next): no actions row; the handles arrive via onReady and
   * completeness via onComplete as the user types.
   */
  hideActions?: boolean;
  onReady?: (stripe: Stripe, card: StripeCardElement) => void;
  onComplete?: (complete: boolean) => void;
}

/**
 * A Stripe Elements card input. Stripe.js is loaded on demand (only when
 * a card form is actually opened) with the team's publishable key.
 */
export const StripeCardForm: React.FC<StripeCardFormProps> = ({
  getStripe,
  submitLabel,
  busy = false,
  error = null,
  onSubmit,
  onCancel,
  cancelLabel = 'Cancel',
  hideActions = false,
  onReady,
  onComplete,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [card, setCard] = useState<StripeCardElement | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let mountedCard: StripeCardElement | null = null;
    getStripe()
      .then((stripeJs) => {
        if (cancelled || !stripeJs || !containerRef.current) return;
        // The card element renders in a Stripe-hosted iframe that can't
        // inherit page CSS, so resolve the theme variables and pass them in
        const rootStyles = getComputedStyle(document.documentElement);
        const cssVar = (name: string, fallback: string) =>
          rootStyles.getPropertyValue(name).trim() || fallback;
        const elements = stripeJs.elements();
        mountedCard = elements.create('card', {
          style: {
            base: {
              color: cssVar('--text-color', '#000000'),
              iconColor: cssVar('--secondary-text-color', '#6b7280'),
              '::placeholder': {
                color: cssVar('--secondary-text-color', '#6b7280'),
              },
            },
            invalid: { color: '#e34948', iconColor: '#e34948' },
          },
        });
        mountedCard.mount(containerRef.current);
        if (onComplete) {
          mountedCard.on('change', (event) => onComplete(event.complete));
        }
        setStripe(stripeJs);
        setCard(mountedCard);
        onReady?.(stripeJs, mountedCard);
      })
      .catch((err) => {
        console.error('Failed to load Stripe.js:', err);
        if (!cancelled) setLoadError('Failed to load the payment form');
      });
    return () => {
      cancelled = true;
      mountedCard?.destroy();
    };
  }, [getStripe]);

  return (
    <div className="billing-card-form">
      {!card && !loadError && (
        <div className="billing-modal-loading" aria-label="Loading" />
      )}
      <div
        ref={containerRef}
        className="billing-stripe-card"
        data-testid="stripe-card-element"
      />
      {(loadError || error) && (
        <div className="projects-error">{loadError || error}</div>
      )}
      <p className="descriptionText">
        <Link to="/privacy-policy">Privacy</Link>: Stripe processes card
        details; Delegate does not store the full card number.
      </p>
      {!hideActions && (
        <div className={onCancel ? 'billing-card-form-actions' : undefined}>
          {onCancel && (
            <button
              className="lightButton"
              data-testid="card-form-cancel"
              disabled={busy}
              onClick={onCancel}>
              {cancelLabel}
            </button>
          )}
          <AsyncButton
            className="primaryButton"
            data-testid="stripe-card-submit"
            disabled={!stripe || !card}
            loading={busy}
            loadingText="Processing..."
            onClick={() => stripe && card && onSubmit?.(stripe, card)}>
            {submitLabel}
          </AsyncButton>
        </div>
      )}
    </div>
  );
};

interface MockCardFormProps {
  submitLabel?: string;
  busy?: boolean;
  error?: string | null;
  onSubmit?: () => void;
  /** When set, renders a secondary button to the left of the submit button. */
  onCancel?: () => void;
  cancelLabel?: string;
  /** Headless mode: no actions row; the caller's own button commits. */
  hideActions?: boolean;
}

/** Fake, disabled card form for the mock billing provider. */
export const MockCardForm: React.FC<MockCardFormProps> = ({
  submitLabel,
  busy = false,
  error = null,
  onSubmit,
  onCancel,
  cancelLabel = 'Cancel',
  hideActions = false,
}) => (
  <div className="billing-card-form">
    <p className="descriptionText">
      Test billing provider — no real card is charged.
    </p>
    <input disabled value="4242 4242 4242 4242" aria-label="Card number" />
    <div className="billing-mock-card-row">
      <input disabled value="12/34" aria-label="Expiration" />
      <input disabled value="123" aria-label="CVC" />
    </div>
    {error && <div className="projects-error">{error}</div>}
    {!hideActions && (
      <div className={onCancel ? 'billing-card-form-actions' : undefined}>
        {onCancel && (
          <button
            className="lightButton"
            data-testid="card-form-cancel"
            disabled={busy}
            onClick={onCancel}>
            {cancelLabel}
          </button>
        )}
        <AsyncButton
          className="primaryButton"
          data-testid="mock-card-submit"
          loading={busy}
          loadingText="Processing..."
          onClick={onSubmit}>
          {submitLabel}
        </AsyncButton>
      </div>
    )}
  </div>
);
